import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ApiError } from '@/lib/http/client';
import { surfaceDisplayStatus, type SurfaceEntry } from '@/lib/sync/document-surfaces';
import { getProposal, type Proposal } from '@/lib/templates/api';

export type ProposalState =
  | { kind: 'none' }
  | { kind: 'loading' }
  | { kind: 'stale' }
  | { kind: 'error' }
  | { kind: 'ready'; proposal: Proposal };

type Settled = Exclude<ProposalState, { kind: 'none' } | { kind: 'loading' }>;

interface Held {
  surfaceId: string;
  attempt: number;
  result: Settled;
}

function reportReadFailure(error: unknown): void {
  if (error instanceof ApiError && error.status === 403) {
    console.warn('proposal read refused', error);
    return;
  }
  console.error('proposal read failed', error);
  Sentry.captureException(error);
}

export function useProposal(
  entityId: string,
  entry: SurfaceEntry | null,
  canEdit: boolean,
): { state: ProposalState; refresh: () => void } {
  const surfaceId = entry && canEdit && surfaceDisplayStatus(entry) === 'proposed' ? entry.id : null;
  const [attempt, setAttempt] = useState(0);
  const [held, setHeld] = useState<Held | null>(null);

  useEffect(() => {
    if (surfaceId === null) return;
    let current = true;
    getProposal(entityId, surfaceId).then(
      (proposal) => {
        if (!current) return;
        setHeld({
          surfaceId,
          attempt,
          result: proposal ? { kind: 'ready', proposal } : { kind: 'stale' },
        });
      },
      (error: unknown) => {
        if (!current) return;
        reportReadFailure(error);
        setHeld({ surfaceId, attempt, result: { kind: 'error' } });
      },
    );
    return () => {
      current = false;
      setHeld(null);
    };
  }, [entityId, surfaceId, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  let state: ProposalState = { kind: 'none' };
  if (surfaceId !== null) {
    state = held && held.surfaceId === surfaceId && held.attempt === attempt ? held.result : { kind: 'loading' };
  }
  return { state, refresh };
}
